import type { WorkerRunSummary } from '@/types/process'

export * from './date'
export * from './entity'
export * from './error'
export * from './iiif'
export * from './polygon'
export * from './process'
export * from './rights'
export * from './text'

/**
 * Given any value, this always returns an array.
 * Falsy values (false, NaN, undefined, null, '', 0, …) will return [].
 * Objects become an array of their values, and other primitives become [value].
 */
export function ensureArray<T>(
  value: T | ArrayLike<T> | { [key: string | number | symbol]: T },
): T[] {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'object') return Object.values(value)
  return [value]
}

type Color = [red: number, green: number, blue: number]

/**
 * Split an hexadecimal RGB color code into numbers from 0 to 255 representing each component.
 * @param hex Hexadecimal color code.
 * @returns Three integers for the three components of the color.
 */
export function hexColorComponents(hex: string): Color {
  hex = hex.replace('#', '')
  return [
    parseInt(hex.substring(0, 2), 16),
    parseInt(hex.substring(2, 4), 16),
    parseInt(hex.substring(4, 6), 16),
  ]
}

/**
 * Get a CSS RGBA color from an hexadecimal color string
 *
 * @param hex A color expressed in hexadecimal format (`#RRGGBB` or `RRGGBB`)
 * @param opacity Opacity, in percents (0 to 100)
 * @returns A color expressed using the `rgba()` CSS syntax
 */
export function colorFromHex(hex: string, opacity: number = 100): string {
  return `'rgba(${hexColorComponents(hex).join(',')},${opacity / 100})`
}

/**
 * Determine whether a color is considered light or dark,
 * which can help in choosing a properly constrasted text or background color.
 * @param hex Hexadecimal color code.
 * @returns Whether the color is light.
 * @link https://stackoverflow.com/a/3943023/5990435
 */
export function isLightColor(hex: string): boolean {
  const [red, green, blue] = hexColorComponents(hex)
  return red * 0.299 + green * 0.587 + blue * 0.114 > 186
}

/**
 * Convert a float representing a size in bytes to a human-readable string.
 *
 * @param bytes Size in bytes.
 * @returns String expressing the size with a multiple of bytes.
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]
}

export interface PaginationParams {
  page?: string
  cursor?: string
}

/*
 * Return pagination parameters within a given URL.
 *
 * @param {url} the URL to be passed.
 * @returns {object} Object containing the different pagination query parameters.
 */
export const getPaginationParams = (url: string | URL): PaginationParams => {
  const searchString = new URL(url).search

  const search = new URLSearchParams(searchString)
  const page = search.get('page')
  const cursor = search.get('cursor')

  const params: PaginationParams = {}
  if (page !== null) params.page = page
  if (cursor !== null) params.cursor = cursor

  return params
}

export const hasWorkerRun = <T extends { worker_run: WorkerRunSummary | null }>(
  mlResult: T,
): mlResult is T & { worker_run: WorkerRunSummary } => mlResult.worker_run !== null
